import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'

const parseFilenameFromContentDisposition = (
    contentDisposition: string | undefined,
    fallback: string,
): string => {
    if (!contentDisposition) {
        return fallback
    }
    const match = /filename="([^"]+)"/.exec(contentDisposition)
    return match?.[1] ?? fallback
}

export const downloadInspectionReport = async (
    leaseId: number,
    apartmentId?: number,
    fallbackFilename = 'Inspection-Report.pdf',
): Promise<void> => {
    const params: Record<string, number> = { lease_id: leaseId }
    if (apartmentId != null) {
        params.apartment_id = apartmentId
    }

    const res = await axiosInstance.get('inspection/tenant-inspection-report/', {
        params,
        responseType: 'blob',
    })

    const filename = parseFilenameFromContentDisposition(
        res.headers['content-disposition'],
        fallbackFilename,
    )

    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(url)
}

export const useInspectionReport = (
    leaseId: number | null | undefined,
    apartmentId: number | null | undefined,
) => {
    const [isDownloading, setIsDownloading] = useState(false)

    const downloadReport = useCallback(async () => {
        if (leaseId == null || isDownloading) {
            return
        }
        setIsDownloading(true)
        try {
            await downloadInspectionReport(leaseId, apartmentId ?? undefined)
        } catch {
            toast.error('Error downloading inspection report')
        } finally {
            setIsDownloading(false)
        }
    }, [leaseId, apartmentId, isDownloading])

    return { isDownloading, downloadReport }
}
